'use client';

import { useDeferredValue } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  searchUsersForImpersonationAction,
  type ImpersonateUserResult,
} from '../server/quick-impersonate-actions';

interface UseUserSearchOptions {
  query: string;
  enabled?: boolean;
}

interface UseUserSearchReturn {
  users: ImpersonateUserResult[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Hook for searching users for impersonation
 *
 * Features:
 * - Only searches when query is 2+ characters
 * - Uses useDeferredValue for debouncing
 * - Caches results for 2 minutes
 * - Returns up to 5 users
 *
 * @example
 * ```tsx
 * const [query, setQuery] = useState('');
 * const { users, isLoading } = useUserSearch({ query });
 * ```
 */
export function useUserSearch({
  query,
  enabled = true,
}: UseUserSearchOptions): UseUserSearchReturn {
  // Debounce the query using React 19 pattern
  const debouncedQuery = useDeferredValue(query);

  const {
    data,
    isLoading,
    isError,
    error: queryError,
  } = useQuery({
    queryKey: ['user-search-impersonate', debouncedQuery],
    queryFn: async () => {
      const result = await searchUsersForImpersonationAction(debouncedQuery);

      if (!result.success) {
        throw new Error(result.error || 'Search failed');
      }

      return result.data ?? [];
    },
    enabled: enabled && debouncedQuery.length >= 2,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return {
    users: data ?? [],
    isLoading: isLoading && debouncedQuery.length >= 2,
    isError,
    error: queryError instanceof Error ? queryError : null,
  };
}
